import { useResetOnClose } from "@/lib/useResetOnClose";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Wand2 } from "lucide-react";
import { confirmAction } from "@/lib/dialogs";
import { api } from "@/lib/api";
import { usePermissions } from "@/lib/permissions";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

// An organisation admin is set on the user, never through a designation.
const ORG_ROLES = ["PORTFOLIO_LEAD", "MEMBER", "ACCOUNTS", "PROCUREMENT", "STORES", "HR", "FINANCE_APPROVER"];
const PROJECT_ROLES = ["PROJECT_MANAGER", "ENGINEERING", "PROCUREMENT", "SITE", "VIEWER"];
const pretty = (s) => (s || "").replaceAll("_", " ");
const EMPTY = { name: "", org_role: "MEMBER", project_role: "VIEWER", is_active: true };

export default function Designations() {
  const { can } = usePermissions();
  const canCreateDesignation = can("DESIGNATIONS", "create");
  const canEditDesignation = can("DESIGNATIONS", "edit");
  const canDeleteDesignation = can("DESIGNATIONS", "delete");
  const remove = async (r) => {
    if (!(await confirmAction({ title: `Delete the designation ${r.name}?`, message: "It is removed from the list for good.", confirmLabel: "Delete" }))) return;
    try { await api.delete(`/masters/designations/${r.id}`); toast.success(`${r.name} deleted`); setRows((prev) => prev.filter((x) => x.id !== r.id)); }
    catch (err) { toast.error(getErrorMessage(err)); }
  };
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);
  useResetOnClose(Boolean(open), () => { setForm(EMPTY); });      // closed without saving: opens fresh next time

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/masters/designations");
      setRows(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, org_role: r.org_role, project_role: r.project_role, is_active: r.is_active });
    setOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.patch(`/masters/designations/${editing.id}`, form);
      else await api.post("/masters/designations", form);
      toast.success(editing ? "Designation updated" : "Designation added");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (r, is_active) => {
    try {
      await api.patch(`/masters/designations/${r.id}`, { is_active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const seed = async () => {
    setSeeding(true);
    try {
      const { data } = await api.post("/masters/designations/seed-defaults");
      toast.success(data.added ? `${data.added} standard designations added` : "All standard designations are already there");
      setRows(data.designations);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="designations-page">
      <PageHeader
        title="Designations"
        description="Job titles and the access each one starts with. Picking a designation while adding a user fills in their role and project role — you can still change both for that person."
        action={canCreateDesignation && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={seed} disabled={seeding} data-testid="seed-designations-button">
              {seeding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />} Add standard titles
            </Button>
            <Button onClick={openCreate} data-testid="add-designation-button"><Plus className="h-4 w-4" /> Add designation</Button>
          </div>
        )}
      />

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <div className="rounded-xl border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Designation</TableHead>
                <TableHead>Organisation role</TableHead>
                <TableHead>Default project role</TableHead>
                <TableHead className="text-right">People</TableHead>
                <TableHead className="text-center">Active</TableHead>
                {(canEditDesignation || canDeleteDesignation) && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`designation-row-${r.name}`}>
                  <TableCell className="font-medium text-foreground">{r.name}</TableCell>
                  <TableCell><Badge variant="secondary">{pretty(r.org_role)}</Badge></TableCell>
                  <TableCell><Badge variant="outline">{pretty(r.project_role)}</Badge></TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{r.user_count}</TableCell>
                  <TableCell className="text-center">
                    <Switch checked={r.is_active} disabled={!canEditDesignation} onCheckedChange={(v) => toggleActive(r, v)} />
                  </TableCell>
                  {(canEditDesignation || canDeleteDesignation) && (
                    <TableCell className="text-right">
                      {canEditDesignation && <Button variant="ghost" size="sm" onClick={() => openEdit(r)} data-testid={`edit-designation-${r.name}`}>
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>}
                      {canDeleteDesignation && <Button variant="ghost" size="sm" title={r.user_count ? `${r.user_count} people have it` : "Delete"} onClick={() => remove(r)} data-testid={`delete-designation-${r.name}`}>
                        <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                      </Button>}
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-sm text-muted-foreground">
                    No designations yet — use “Add standard titles” to load the SARPL list.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="designation-form-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit designation" : "Add designation"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Designation</Label>
              <Input className="mt-1.5" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="designation-name-input" />
            </div>
            <div>
              <Label>Organisation role</Label>
              <Select value={form.org_role} onValueChange={(v) => setForm({ ...form, org_role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="designation-org-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ORG_ROLES.map((r) => <SelectItem key={r} value={r}>{pretty(r)}</SelectItem>)}</SelectContent>
              </Select>
              <p className="mt-1.5 text-xs text-muted-foreground">Decides which modules they can open (see the Permission Matrix).</p>
            </div>
            <div>
              <Label>Default project role</Label>
              <Select value={form.project_role} onValueChange={(v) => setForm({ ...form, project_role: v })}>
                <SelectTrigger className="mt-1.5" data-testid="designation-project-role"><SelectValue /></SelectTrigger>
                <SelectContent>{PROJECT_ROLES.map((r) => <SelectItem key={r} value={r}>{pretty(r)}</SelectItem>)}</SelectContent>
              </Select>
              <p className="mt-1.5 text-xs text-muted-foreground">What they can do inside a project they are added to.</p>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || form.name.trim().length < 2} data-testid="designation-save-button">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Save changes" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
